import React, { useEffect, useState } from 'react';

/**
 * ErrorDisplay - Reusable error and success notification component
 * Shows dismissible alerts with auto-hide functionality
 */
const ErrorDisplay = ({ 
  error, 
  success, 
  onDismiss, 
  autoHide = true, 
  hideDelay = 5000,
  position = 'top-center' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(null);

  // Update message when props change
  useEffect(() => {
    if (error) {
      setCurrentMessage({ type: 'error', message: error });
      setIsVisible(true);
    } else if (success) {
      setCurrentMessage({ type: 'success', message: success });
      setIsVisible(true);
    } else {
      setIsVisible(false);
      setCurrentMessage(null);
    }
  }, [error, success]);

  // Auto-hide functionality
  useEffect(() => {
    if (isVisible && autoHide && hideDelay > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, hideDelay);

      return () => clearTimeout(timer);
    }
  }, [isVisible, autoHide, hideDelay]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!currentMessage || !isVisible) {
    return null;
  }

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'error-display-top-left';
      case 'top-right':
        return 'error-display-top-right';
      case 'bottom-center':
        return 'error-display-bottom-center';
      case 'bottom-left':
        return 'error-display-bottom-left';
      case 'bottom-right':
        return 'error-display-bottom-right';
      default:
        return 'error-display-top-center';
    }
  };

  return (
    <div className={`error-display ${getPositionClasses()} ${currentMessage.type}`}>
      <div className="error-content">
        <div className="error-icon">
          {currentMessage.type === 'error' ? '❌' : '✅'}
        </div>
        <div className="error-message">
          {typeof currentMessage.message === 'string' 
            ? currentMessage.message 
            : currentMessage.message.title || 'An error occurred'
          }
          {currentMessage.message.suggestion && (
            <div className="error-suggestion">
              {currentMessage.message.suggestion}
            </div>
          )}
        </div>
        {onDismiss && (
          <button className="error-dismiss" onClick={handleDismiss}>
            ×
          </button>
        )}
      </div>

      <style jsx>{`
        .error-display {
          position: fixed;
          z-index: 9999;
          max-width: 400px;
          width: auto;
          min-width: 300px;
          animation: slideIn 0.3s ease-out;
        }

        .error-display-top-center {
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
        }

        .error-display-top-left {
          top: 20px;
          left: 20px;
        }

        .error-display-top-right {
          top: 20px;
          right: 20px;
        }

        .error-display-bottom-center {
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
        }

        .error-display-bottom-left {
          bottom: 20px;
          left: 20px;
        }

        .error-display-bottom-right {
          bottom: 20px;
          right: 20px;
        }

        .error-content {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px 20px;
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid transparent;
        }

        .error-display.error .error-content {
          background-color: rgba(244, 67, 54, 0.95);
          color: white;
          border-color: rgba(244, 67, 54, 0.3);
        }

        .error-display.success .error-content {
          background-color: rgba(76, 175, 80, 0.95);
          color: white;
          border-color: rgba(76, 175, 80, 0.3);
        }

        .error-icon {
          font-size: 20px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .error-message {
          flex: 1;
          font-size: 14px;
          line-height: 1.4;
          font-weight: 500;
        }

        .error-suggestion {
          margin-top: 8px;
          font-size: 12px;
          opacity: 0.9;
          font-weight: 400;
        }

        .error-dismiss {
          background: transparent;
          border: none;
          color: inherit;
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s ease;
          flex-shrink: 0;
        }

        .error-dismiss:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .error-display {
            max-width: calc(100vw - 32px);
            min-width: auto;
            width: calc(100vw - 32px);
            left: 16px !important;
            right: 16px !important;
            transform: none !important;
          }

          .error-display-top-center,
          .error-display-top-left,
          .error-display-top-right {
            top: 16px;
          }

          .error-display-bottom-center,
          .error-display-bottom-left,
          .error-display-bottom-right {
            bottom: 16px;
          }

          .error-content {
            padding: 14px 16px;
          }

          .error-message {
            font-size: 14px;
          }

          .error-suggestion {
            font-size: 12px;
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        }

        /* Safe area support for notched devices */
        @supports (padding: max(0px)) {
          .error-display-top-center,
          .error-display-top-left,
          .error-display-top-right {
            top: max(20px, env(safe-area-inset-top) + 10px);
          }

          .error-display-bottom-center,
          .error-display-bottom-left,
          .error-display-bottom-right {
            bottom: max(20px, env(safe-area-inset-bottom) + 10px);
          }
        }
      `}</style>
    </div>
  );
};

export default ErrorDisplay;